import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getFilters, getFilterDetail } from "@/shared/infrastructure/adapters/filterAdapter";
import { criteriaToText } from "@/shared/utils/criteriaToText";
import { logger } from "@/infrastructure/services/LoggerService";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { formatDate } from "@/shared/utils/date-formatting";
export function useAdvancedFilterLibrary(options) {
    const { entity, application, builderFields } = options;
    const { t, locale } = useI18n();
    // Unconditional store read (used by resolveMeta's date formatting, R30) — any test that mounts
    // this composable, directly or via a component, needs an active Pinia (`setActivePinia(createPinia())`).
    const profileStore = useProfileStore();
    const summaries = ref([]);
    const isLoading = ref(false);
    const hasError = ref(false);
    const activeTab = ref("personal");
    const search = ref("");
    const selectedId = ref(null);
    const tooltipTextById = ref({});
    // Tracks ids whose tooltip loaded SUCCESSFULLY, so a failed fetch can be retried
    // (a cached error text must not permanently block re-fetch on the next hover).
    const loadedTooltipIds = new Set();
    function resolveName(f) {
        // System filter names are i18n keys `{entity}.filters.{key}`; others are literals (mirrors useFilters).
        return f.visibility === "System"
            ? t(`${entity}.filters.${f.name.split(".").pop() ?? f.name}`)
            : f.name;
    }
    function resolveMeta(f) {
        // Gate on visibility, not on a null ownerId: the OPREF seeder would set ownerId = null on
        // every seeded row regardless of visibility, and Workspace filters DO carry a real owner (the
        // administrator who created them) — only System filters are ownerless by contract (D3).
        if (f.visibility === "System")
            return t("advancedFilterLibrary.meta.createdByEudonet");
        const segments = [];
        if (f.ownerDisplayName) {
            segments.push(t("advancedFilterLibrary.meta.createdBy", { name: f.ownerDisplayName }));
        }
        // CONSTRAINT: the OPREF audit interceptor stamps UpdatedAt = CreatedAt and UpdatedBy = the
        // creator on INSERT, so a never-modified filter carries a complete, false modification trail.
        // Drop this guard and every brand-new filter claims it was modified on its creation date.
        // Both timestamps must be present (an old BFF pod mid rolling-deploy omits the new fields
        // entirely) so formatDate's "–" fallback can never surface, and the comparison is by time
        // VALUE rather than raw string so a future per-field serialization change (or a move to
        // DateTimeOffset) cannot silently make every new filter claim it was modified on creation.
        if (f.createdAt && f.updatedAt && new Date(f.updatedAt).getTime() !== new Date(f.createdAt).getTime()) {
            const date = formatDate(f.updatedAt, locale.value, profileStore.profile?.dateFormat);
            segments.push(f.updatedByDisplayName
                ? t("advancedFilterLibrary.meta.modifiedByOn", { name: f.updatedByDisplayName, date })
                : t("advancedFilterLibrary.meta.modifiedOn", { date }));
        }
        if (segments.length === 0) {
            // Terminal fallback — the meta line must never render empty (regression from the OP-6508
            // placeholder, which always had content): a rolling deploy skew serves an old BFF payload
            // missing every new field, an OPUS outage blanks ownerDisplayName, and a deleted owner
            // blanks it permanently. `visibility` predates this story and survives every payload shape.
            return t(f.visibility === "Personal" ? "advancedFilterLibrary.meta.createdByMe" : "advancedFilterLibrary.meta.shared");
        }
        return segments.join(" · ");
    }
    const allItems = computed(() => summaries.value.map((f) => ({
        id: f.id,
        name: resolveName(f),
        visibility: f.visibility,
        meta: resolveMeta(f),
        canEdit: f.canEdit,
        canDelete: f.canDelete,
        canManageAccess: f.canManageAccess,
    })));
    // Tab taxonomy is fixed by the OP-6508 product decision (spec D7), NOT by raw visibility:
    //  - "Mes filtres"      = my own Personal filters.
    //  - "Filtres partagés" = personal filters of OTHER users shared with me. OPREF returns them
    //    alongside my own Personal filters (OP-6790); the two are told apart by `canManageAccess`,
    //    which the server sets only for a Personal filter the caller owns. It is deliberately NOT
    //    the Workspace set.
    //  - "Filtres Eudonet"  = System + Workspace (Eudonet / org-level presets).
    const buckets = computed(() => ({
        personal: allItems.value.filter((i) => i.visibility === "Personal" && i.canManageAccess),
        shared: allItems.value.filter((i) => i.visibility === "Personal" && !i.canManageAccess),
        eudonet: allItems.value.filter((i) => i.visibility === "System" || i.visibility === "Workspace"),
    }));
    const counts = computed(() => ({
        personal: buckets.value.personal.length,
        shared: buckets.value.shared.length,
        eudonet: buckets.value.eudonet.length,
    }));
    const isSearching = computed(() => search.value.trim().length > 0);
    const visibleItems = computed(() => {
        if (isSearching.value) {
            const q = search.value.trim().toLowerCase();
            return allItems.value.filter((i) => i.name.toLowerCase().includes(q));
        }
        return buckets.value[activeTab.value];
    });
    const resultCount = computed(() => visibleItems.value.length);
    const emptyState = computed(() => {
        // A failed load must be distinguishable from an empty library (R47): surface the
        // error/loading flags here so the list renders a dedicated state instead of the
        // "create your first filter" empty CTA on a backend outage.
        if (hasError.value)
            return "error";
        if (isLoading.value && visibleItems.value.length === 0)
            return "loading";
        if (visibleItems.value.length > 0)
            return null;
        if (isSearching.value)
            return "no-results";
        return `${activeTab.value}-empty`;
    });
    // Drop the selection when it is no longer visible (tab switch or search narrowing),
    // so Apply can never apply a filter absent from the current view.
    watch(visibleItems, (items) => {
        if (selectedId.value && !items.some((i) => i.id === selectedId.value)) {
            selectedId.value = null;
        }
    });
    async function load() {
        isLoading.value = true;
        hasError.value = false;
        try {
            summaries.value = await getFilters(entity, application);
        }
        catch (e) {
            summaries.value = [];
            hasError.value = true;
            logger.error("useAdvancedFilterLibrary: failed to load saved filters", e);
        }
        finally {
            isLoading.value = false;
        }
    }
    async function loadTooltip(id) {
        if (loadedTooltipIds.has(id))
            return;
        try {
            const detail = await getFilterDetail(id);
            const ast = detail.criteria;
            tooltipTextById.value = {
                ...tooltipTextById.value,
                [id]: criteriaToText(ast, builderFields.value, t),
            };
            loadedTooltipIds.add(id);
        }
        catch (e) {
            // Show the error text but do NOT mark as loaded, so the next hover retries.
            tooltipTextById.value = { ...tooltipTextById.value, [id]: t("advancedFilterLibrary.tooltip.error") };
            logger.error("useAdvancedFilterLibrary: failed to load filter detail", e);
        }
    }
    function select(id) {
        selectedId.value = id;
    }
    function reset() {
        search.value = "";
        selectedId.value = null;
        activeTab.value = "personal";
        tooltipTextById.value = {};
        loadedTooltipIds.clear();
    }
    return {
        activeTab,
        search,
        selectedId,
        isLoading,
        hasError,
        counts,
        isSearching,
        visibleItems,
        resultCount,
        emptyState,
        tooltipTextById,
        load,
        loadTooltip,
        select,
        reset,
    };
}
